<template>
	<div class="max-w-4xl mx-auto text-xl text-center px-2 xl:px-0 flex flex-col gap-8 sm:gap-8">
		<!-- Header Section -->
		<div class="flex flex-col gap-2 sm:gap-4 pt-2">
			<h1 class="text-5xl sm:text-6xl leading-tight text-center text-primary font-semibold">
				{{ showPricingCards ? $t('get-fiber.header.internet-available.headline-1') : getFiberHeadline1 }}
				<span class="block text-highlighted font-semibold text-4xl sm:text-5xl">
					{{ showPricingCards ? $t('get-fiber.header.internet-available.headline-2') : getFiberHeadline2 }}
				</span>
			</h1>
			<div
				v-if="internetUnavailable"
				class="text-lg text-center font-medium text-default max-w-2xl mx-auto text-pretty"
			>
				<p v-if="getFiberSubheadline1">
					{{ getFiberSubheadline1 }}
				</p>
				<p v-if="getFiberSubheadline2">
					{{ getFiberSubheadline2 }}
				</p>
			</div>
		</div>
		<!-- Construction Status Section -->
		<div>
			<template v-if="showConstructionStatus">
				<div
					v-if="underConstruction"
					class="text-lg text-center pb-6 sm:pb-8 font-medium text-default max-w-2xl mx-auto text-pretty"
				>
					<p>
						{{ choosePlanP1 }}
					</p>
					<p>
						{{ choosePlanP2 }}
					</p>
				</div>
			</template>
			<!-- Pricing Cards Section -->
			<section v-if="showPricingCards">
				<div class="flex flex-col gap-4 pt-0 pb-2">
					<h2 class="text-4xl text-center text-primary font-semibold">
						{{ $t('get-fiber.choose-plan.pricing-cards.h2') }}
					</h2>
					<div class="text-highlighted font-medium max-w-xl w-full mx-auto px-8 sm:px-0">
						{{ $t('get-fiber.choose-plan.pricing-cards.subtitle') }}
					</div>
				</div>
				<div v-if="selectedTierId">
					<PricingCard
						:tier="selectedTier"
						:selected-tier-index="selectedTierId"
						:button-text="$t('get-fiber.choose-plan.pricing-cards.selected-plan')"
						disable-button
					/>
					<UButton
						color="primary"
						variant="ghost"
						:label="$t('get-fiber.choose-plan.pricing-cards.change-plan')"
						:ui="{
							label: 'text-sm font-normal'
						}"
						class="hover:underline decoration-primary decoration-2 underline-offset-2 mt-4"
						@click="setTier(selectedTierId)"
					/>
				</div>
				<div v-else>
					<PricingCardSection
						:billables="billables"
						class="min-h-[512px]"
						@set-tier="setTier"
					/>
					<p class="mt-4 sm:mt-12 mb-4 px-1 sm:px-0 text-pretty leading-0 sm:text-left">
						<span class="flex flex-col text-sm">
							{{ $t('get-fiber.choose-plan.pricing-cards.disclaimer') }}
						</span>
						<span class="text-primary text-base">
							{{ $t('get-fiber.choose-plan.pricing-cards.more-bandwidth') }}
						</span>
					</p>
					<div class="relative p-4 text-left">
						<div
							id="installation_costs"
							class="absolute -top-16"
						/>
						<h4>
							{{ $t('get-fiber.choose-plan.pricing-cards.installation-costs') }}
						</h4>
						<ul class="mx-4 text-sm list-disc">
							<li>
								<strong>
									{{ $t('get-fiber.choose-plan.pricing-cards.li-1-bold') }}
								</strong> {{ $t('get-fiber.choose-plan.pricing-cards.li-1') }}
							</li>
							<li>
								{{ $t('get-fiber.choose-plan.pricing-cards.li-2') }}
							</li>
							<li>
								{{ $t('get-fiber.choose-plan.pricing-cards.li-3') }}
							</li>
						</ul>
					</div>
				</div>
			</section>
		</div>
		<PageDivider v-if="showPricingCards" />
		<!-- Signup Form Section -->
		<div>
			<h3
				v-if="showPricingCards"
				class="w-full text-3xl font-semibold tracking-tight text-center text-highlighted"
			>
				{{ $t('get-fiber.form.your-information') }}
			</h3>
			<SignupForm
				:package-level="selectedTierId"
				:moving-in="movingIn"
				:billables="billables"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	LazyModalFcc, LazyModalFccSlider
} from '#components'
import { useGA4Tracking } from '@/composables/useGA4Tracking'
import { useAddressLocalStorage } from '@/composables/useLocalStorage'
import type { MappedPackage } from '@/types/components/signup'
import { useWindowSize } from '@vueuse/core'

const { /* locale, */ t } = useI18n({
	useScope: 'global'
})

const props = defineProps({
	internetAvailable: {
		type: Boolean,
		default: false
	},
	underConstruction: {
		type: Boolean,
		default: false
	},
	internetUnavailable: {
		type: Boolean,
		default: false
	},
	movingIn: {
		type: Boolean,
		default: false
	}
})

const {
	internetAvailable,
	underConstruction,
	internetUnavailable,
	movingIn
} = toRefs(props)

const { trackServicePlan } = useGA4Tracking()
const { width } = useWindowSize()

const isMobile = computed(() => width.value < 768)
const isDesktop = computed(() => width.value >= 1024)

const { cachedAddressResponse } = useAddressLocalStorage()

const billables = computed(() => cachedAddressResponse?.value?.billables || [])

const selectedTierId = ref(0)

const overlay = useOverlay()

const modal = overlay.create(LazyModalFcc, {
	props: {
		selectedTierId: selectedTierId.value,
		billables: billables.value
	}
})

const modalSlider = overlay.create(LazyModalFccSlider, {
	props: {
		selectedTierId: selectedTierId.value,
		billables: billables.value
	}
})

watch(selectedTierId, (newValue: number) => {
	if (newValue) {
		modal.patch({
			selectedTierId: newValue
		})
		modalSlider.patch({
			selectedTierId: newValue
		})
		if (!selectedTier.value) return
		trackServicePlan('select', selectedTierId.value, selectedTier.value.packageSpeed, selectedTier.value.price)
	}
})

async function openFccModal() {
	const instance = modal.open()

	const updatedTierId = await instance.result as number

	if (updatedTierId) {
		selectedTierId.value = updatedTierId
		return
	}
}

async function openFccSlider() {
	const instance = modalSlider.open()

	const updatedTierId = await instance.result as number

	if (updatedTierId) {
		selectedTierId.value = updatedTierId
		return
	}
}

const setTier = (tier: number) => {
	nextTick(async () => {
		if (isMobile.value) {
			await openFccSlider()
			return
		}
		if (isDesktop.value) {
			await openFccModal()
			return
		}
		// Fallback for other devices
		await openFccModal()
		if (!selectedTier.value) return
		trackServicePlan('view', tier, selectedTier.value.packageSpeed, selectedTier.value.price)
	})
}

const selectedTier = computed(() => billables.value.find((billable: MappedPackage) => billable.id === selectedTierId.value))
const showPricingCards = computed(() => {
	return internetAvailable.value || underConstruction.value || movingIn.value
})

const getFiberHeadline1 = computed(() => {
	if (internetAvailable.value) {
		return t('get-fiber.header.internet-available.headline-1')
	}
	if (underConstruction.value) {
		return t('get-fiber.header.under-construction.headline-1')
	}
	if (internetUnavailable.value) {
		return t('get-fiber.header.internet-unavailable.headline-1')
	}
	return t('get-fiber.header.default.headline-1')
})

const getFiberHeadline2 = computed(() => {
	if (internetAvailable.value) {
		return t('get-fiber.header.internet-available.headline-2')
	}
	if (underConstruction.value) {
		return t('get-fiber.header.under-construction.headline-2')
	}
	if (internetUnavailable.value) {
		return t('get-fiber.header.internet-unavailable.headline-2')
	}
	return t('get-fiber.header.default.headline-2')
})

const getFiberSubheadline1 = computed(() => {
	if (internetUnavailable.value) {
		return t('get-fiber.header.internet-unavailable.subheadline-1')
	}
	return null
})
const getFiberSubheadline2 = computed(() => {
	if (internetUnavailable.value) {
		return '' // t('get-fiber.header.internet-unavailable.subheadline-2')
	}
	return null
})

const showConstructionStatus = computed(() => {
	return internetAvailable.value || underConstruction.value || internetUnavailable.value
})

const choosePlanP1 = computed(() => {
	if (underConstruction.value) {
		return t('get-fiber.choose-plan.under-construction.p1')
	}
	if (internetAvailable.value) {
		return t('get-fiber.choose-plan.internet-available.p1')
	}
	if (internetUnavailable.value) {
		return t('get-fiber.choose-plan.internet-unavailable.p1')
	}
	return null
})

const choosePlanP2 = computed(() => {
	if (underConstruction.value) {
		return t('get-fiber.choose-plan.under-construction.p2')
	}
	if (internetAvailable.value) {
		return t('get-fiber.choose-plan.internet-available.p2')
	}
	if (internetUnavailable.value) {
		return null // t('get-fiber.choose-plan.internet-unavailable.p2')
	}
	return null
})
</script>
